import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useCustomers } from "@/hooks/use-customers";
import { NewCustomerDialog } from "@/components/new-project/NewCustomerDialog";
import {
  PdfOfferUpload,
  type MatchedBudget,
} from "@/components/new-project/PdfOfferUpload";
import { useCreateOfferFromImport } from "@/hooks/use-create-offer-from-import";
import { ImportLineReviewStep } from "@/components/offer/ImportLineReviewStep";
import { SrIcon } from "@/components/ui/SrIcon";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * VIB-1130: Opprett et tilbud fra et importert kunde-tilbud (PDF/bilde).
 * Steg 1 velg kunde (påkrevd), steg 2 Vision-importér budsjett, deretter
 * «Opprett tilbud» → opprett offer + offer_lines og åpne tilbudseditoren.
 */
export function ImportOfferDialog({ open, onOpenChange }: Props) {
  const navigate = useNavigate();
  const { data: customers = [] } = useCustomers();
  const createFromImport = useCreateOfferFromImport();

  const [customerId, setCustomerId] = useState<string | null>(null);
  const [customerPopoverOpen, setCustomerPopoverOpen] = useState(false);
  const [budget, setBudget] = useState<MatchedBudget | null>(null);

  const selectedCustomer = customers.find((c) => c.id === customerId);
  const canSubmit =
    customerId !== null && budget !== null && !createFromImport.isPending;

  function reset() {
    setCustomerId(null);
    setBudget(null);
  }

  function handleOpenChange(next: boolean) {
    if (!next) reset();
    onOpenChange(next);
  }

  async function handleCreate() {
    if (!customerId || !budget) return;
    try {
      const offerId = await createFromImport.mutateAsync({
        customerId,
        customerName: selectedCustomer?.name ?? null,
        budget,
      });
      toast.success("Tilbud opprettet fra import");
      onOpenChange(false);
      reset();
      navigate(`/tilbud/${offerId}`);
    } catch (err) {
      toast.error(`Kunne ikke opprette tilbud: ${(err as Error).message}`);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[85vh] w-[calc(100vw-2rem)] overflow-y-auto overflow-x-hidden sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>Nytt tilbud fra import</DialogTitle>
          <DialogDescription>
            Velg kunde og last opp et tilbud (PDF eller skjermbilde). AI leser
            budsjettet og oppretter et tilbud du kan redigere.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>
              Kunde <span className="text-destructive">*</span>
            </Label>
            <div className="flex gap-2">
              <Popover
                open={customerPopoverOpen}
                onOpenChange={setCustomerPopoverOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="min-w-0 flex-1 justify-between font-normal"
                  >
                    <span className="truncate">{selectedCustomer?.name ?? "Velg kunde…"}</span>
                    <SrIcon
                      name="chevrons-up-down"
                      className="ml-2 h-4 w-4 shrink-0 opacity-50"
                    />
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className="w-[--radix-popover-trigger-width] p-0"
                  align="start"
                >
                  <Command>
                    <CommandInput placeholder="Søk kunder…" />
                    <CommandList>
                      <CommandEmpty>Ingen treff</CommandEmpty>
                      <CommandGroup>
                        {customers.map((c) => (
                          <CommandItem
                            key={c.id}
                            value={c.name}
                            onSelect={() => {
                              setCustomerId(c.id);
                              setCustomerPopoverOpen(false);
                            }}
                          >
                            <SrIcon
                              name="check"
                              className={cn(
                                "mr-2 h-4 w-4",
                                customerId === c.id ? "opacity-100" : "opacity-0",
                              )}
                            />
                            {c.name}
                            {c.country_code && c.country_code !== "NO" && (
                              <span className="ml-2 text-xs text-muted-foreground">
                                {c.country_code}
                              </span>
                            )}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <NewCustomerDialog onCustomerCreated={(id) => setCustomerId(id)} />
            </div>
          </div>

          {!customerId ? (
            <p className="text-sm text-muted-foreground rounded-md border border-dashed p-4 text-center">
              Velg kunde før du laster opp tilbudet.
            </p>
          ) : budget === null ? (
            <PdfOfferUpload
              onOfferSelected={setBudget}
              pdfUploaded={false}
              budgetLinesCount={0}
              onReset={() => setBudget(null)}
              aiProgress={0}
              aiLoading={false}
            />
          ) : (
            <ImportLineReviewStep budget={budget} onChange={setBudget} />
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => handleOpenChange(false)}>
            Avbryt
          </Button>
          {budget !== null && (
            <Button variant="outline" onClick={() => setBudget(null)}>
              Tilbake
            </Button>
          )}
          <Button onClick={handleCreate} disabled={!canSubmit}>
            {createFromImport.isPending && (
              <SrIcon name="loader2" className="mr-2 h-4 w-4 animate-spin" />
            )}
            Opprett tilbud
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
